/**
 * XVELO About Us Page — Motion System
 * Handles: scroll reveal, cursor glow, count-up animations
 */

(function() {
  'use strict';

  const SELECTORS = [
    '.manifesto-row',
    '.stat-grid > div',
    '.brand-grid',
    '.brand-cell',
    '.doc-panel',
    '.criteria-grid',
    '.criteria-card',
    '.timeline-list',
    '.timeline',
    '.team-grid',
    '.team-grid > div',
    '.section-eyebrow-row',
    '[data-reveal]'
  ];

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Global reveal system — adds .in class when elements enter viewport
   */
  function initReveal() {
    const collect = () => {
      const set = new Set();
      SELECTORS.forEach(sel => {
        document.querySelectorAll(sel).forEach(el => set.add(el));
      });
      return set;
    };

    const reveal = (el) => el.classList.add('in');

    // If reduce motion, reveal everything immediately
    if (reduceMotion) {
      collect().forEach(reveal);
      return;
    }

    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          reveal(e.target);
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -4% 0px' });

    const seen = new WeakSet();
    const tracked = new Set();

    const attach = () => {
      collect().forEach(el => {
        if (seen.has(el)) return;
        seen.add(el);
        tracked.add(el);
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          reveal(el);
          tracked.delete(el);
        } else {
          io.observe(el);
        }
      });
    };

    attach();
    setTimeout(attach, 200);
    setTimeout(attach, 800);

    // Fallback: scroll-driven check
    const checkOnScroll = () => {
      if (tracked.size === 0) return;
      const vh = window.innerHeight;
      tracked.forEach(el => {
        if (!el.isConnected) {
          tracked.delete(el);
          return;
        }
        const r = el.getBoundingClientRect();
        if (r.top < vh * 0.96 && r.bottom > 0) {
          reveal(el);
          try { io.unobserve(el); } catch(e) {}
          tracked.delete(el);
        }
      });
    };

    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        checkOnScroll();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    // Safety net: after 4s, force-reveal anything still hidden
    setTimeout(() => {
      tracked.forEach(reveal);
      tracked.clear();
    }, 4000);
  }

  /**
   * Cursor-follow glow for hero section
   */
  function initCursorGlow() {
    const hero = document.querySelector('.hero-canvas');
    if (!hero || reduceMotion) return;

    hero.addEventListener('mousemove', (e) => {
      const r = hero.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 100;
      const y = ((e.clientY - r.top) / r.height) * 100;
      hero.style.setProperty('--mx', x + '%');
      hero.style.setProperty('--my', y + '%');
    });
  }

  /**
   * Count-up animation for stat numbers
   */
  function initCountUp() {
    const statNums = document.querySelectorAll('[data-countup]');
    if (statNums.length === 0) return;

    statNums.forEach(el => {
      const to = parseFloat(el.dataset.countup);
      const decimals = parseInt(el.dataset.decimals || '0', 10);
      const suffix = el.dataset.suffix || '';
      const duration = 1600;

      if (reduceMotion) {
        el.textContent = decimals > 0 ? to.toFixed(decimals) + suffix : Math.round(to).toLocaleString('en-US') + suffix;
        return;
      }

      let started = false;
      const io = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !started) {
            started = true;
            const t0 = performance.now();

            const tick = (t) => {
              const p = Math.min(1, (t - t0) / duration);
              const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
              const val = to * eased;

              if (decimals > 0) {
                el.textContent = val.toFixed(decimals) + suffix;
              } else {
                el.textContent = Math.round(val).toLocaleString('en-US') + suffix;
              }

              if (p < 1) {
                requestAnimationFrame(tick);
              } else {
                if (decimals > 0) {
                  el.textContent = to.toFixed(decimals) + suffix;
                } else {
                  el.textContent = Math.round(to).toLocaleString('en-US') + suffix;
                }
              }
            };

            requestAnimationFrame(tick);
            io.disconnect();
          }
        });
      }, { threshold: 0.4 });

      io.observe(el);
    });
  }

  /**
   * Initialize all animations when DOM is ready
   */
  function init() {
    initReveal();
    initCursorGlow();
    initCountUp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
